import React from 'react';
import { Field } from 'shared/ui/Field';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from 'shared/ui/Select';

export type PickerOption = { value: string; label: string; /** mirror text; defaults to label */ short?: string };

export type PickerProps = {
  id: string;
  label: string;
  ariaLabel?: string;
  gate?: string;
  hint?: React.ReactNode;
  options: PickerOption[];
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  className?: string;
};

/** The one inert mirror primitive (R21): a Field-wrapped Select plus a visually
 *  hidden native <select> + joined-label span. Radix portals its listbox, which
 *  jsdom cannot open without pointer-event stubs, so every feature test asserts
 *  against this mirror's data instead of the portal. `short` lets a caller give
 *  the mirror a terser string than the visible option label (e.g. term months). */
export function Picker({ id, label, ariaLabel, gate, hint, options, value, onChange, disabled, className }: PickerProps) {
  // CONF-218: the trigger clamps a long label to one line; hovering reveals it.
  const selectedLabel = options.find((o) => o.value === value)?.label;
  return (
    <Field label={label} gate={gate} hint={hint} className={className}>
      <Select value={value} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger aria-label={ariaLabel ?? label} title={selectedLabel}><SelectValue /></SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
      <span className="sr-only" aria-hidden="true">
        <span data-testid={`${id}-options`}>{options.map((o) => o.short ?? o.label).join('|')}</span>
        <select data-testid={`${id}-native`} tabIndex={-1} disabled={disabled} value={value} onChange={(e) => onChange(e.target.value)}>
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </span>
    </Field>
  );
}
